/**
 * Real WhatsApp/Viber deep links + icons, extracted from `hero-content.tsx`
 * (Phase 2) so the Kontakt page (Phase 6) can reuse the exact same buttons
 * instead of re-declaring the SVGs. `hero-content.tsx` now imports from
 * here too — see its own comment for why the hero only shows these at
 * `lg:` (top-right, in line with the nav) and falls back to a centered row
 * on small screens.
 */
export const WHATSAPP_HREF = 'https://wa.me/38761722891?text=Zdravo%2C%20zanima%20me%20izrada%20web%20stranice'
export const VIBER_HREF = 'viber://chat?number=%2B38761722891'

export function WhatsAppIcon({ size = 19 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.5 14.4c-.3-.15-1.7-.85-1.95-.94-.26-.1-.45-.15-.65.15-.19.29-.75.94-.92 1.13-.17.2-.34.22-.63.08-.3-.15-1.24-.46-2.36-1.46-.87-.78-1.46-1.74-1.63-2.03-.17-.3-.02-.46.13-.6.13-.14.3-.34.44-.51.15-.17.2-.3.3-.49.1-.2.05-.37-.02-.51-.08-.15-.65-1.58-.9-2.16-.24-.58-.48-.5-.65-.5-.17 0-.36-.02-.56-.02-.19 0-.51.07-.78.37-.26.3-1.02 1-1.02 2.43s1.05 2.82 1.19 3.01c.15.2 2.06 3.14 4.99 4.4.7.3 1.24.48 1.66.61.7.22 1.34.19 1.84.11.56-.08 1.7-.7 1.95-1.37.24-.68.24-1.26.17-1.38-.07-.13-.26-.2-.56-.35z" />
      <path d="M12.04 2C6.58 2 2.13 6.13 2.13 11.27c0 1.99.68 3.83 1.83 5.33L2.5 21l4.53-1.4a10.6 10.6 0 0 0 5.01 1.26h.01c5.46 0 9.91-4.13 9.91-9.27C22 6.13 17.5 2 12.04 2Zm0 16.85h-.01a8.9 8.9 0 0 1-4.34-1.14l-.31-.18-3.23 1 .87-3.08-.2-.32a7.86 7.86 0 0 1-1.28-4.26c0-4.31 3.66-7.82 8.5-7.82 2.27 0 4.4.85 6 2.4a7.4 7.4 0 0 1 2.5 5.42c0 4.31-3.66 7.98-8.5 7.98Z" />
    </svg>
  )
}

export function ViberIcon({ size = 19 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.04 2C6.58 2 2.13 6.13 2.13 11.27c0 2.91 1.39 5.51 3.58 7.27-.08.97-.34 2.62-.4 3-.07.43.16.43.34.32.14-.09 2.18-1.45 3.07-2.04.99.27 2.05.41 3.32.41 5.46 0 9.91-4.13 9.91-9.27C22 6.13 17.5 2 12.04 2Zm5.62 13.36c-.24.42-1.27.95-1.74.99-.45.04-.45.35-2.84-.59-2.4-.94-3.91-3.27-4.03-3.43-.12-.16-.96-1.27-.96-2.42 0-1.15.6-1.71.82-1.95.21-.23.46-.29.61-.29.16 0 .31 0 .45.01.14.01.34-.05.53.41.2.48.67 1.65.73 1.76.06.12.1.26.02.42-.08.16-.13.26-.26.4-.13.14-.27.31-.38.42-.13.12-.26.25-.12.5.15.27.65 1.09 1.41 1.78.97.88 1.79 1.16 2.06 1.29.27.13.43.11.59-.05.16-.16.66-.74.83-1 .17-.25.34-.21.56-.13.23.09 1.45.7 1.7.83.25.13.42.19.48.3.06.11.06.61-.18 1.03Z" />
    </svg>
  )
}

/**
 * `variant="pill"` — full-size labeled pills (original hero style, now also
 * used on the Kontakt page). `variant="compact"` — smaller icon-only
 * circles for the hero's top-right nav-aligned placement, where full pills
 * would compete with the floating nav for space.
 */
export function ContactButtons({
  variant = 'pill',
  className = '',
}: {
  variant?: 'pill' | 'compact'
  className?: string
}) {
  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <a
          href={WHATSAPP_HREF}
          target="_blank"
          rel="noopener"
          aria-label="WhatsApp"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-whatsapp text-white
                     transition-all duration-normal hover:scale-110 hover:shadow-[0_0_16px_rgba(37,211,102,0.5)] active:scale-95"
        >
          <WhatsAppIcon size={16} />
        </a>
        <a
          href={VIBER_HREF}
          aria-label="Viber"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-viber text-white
                     transition-all duration-normal hover:scale-110 hover:shadow-[0_0_16px_rgba(115,96,242,0.5)] active:scale-95"
        >
          <ViberIcon size={16} />
        </a>
      </div>
    )
  }

  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <a
        href={WHATSAPP_HREF}
        target="_blank"
        rel="noopener"
        className="inline-flex items-center gap-2 rounded-pill bg-whatsapp px-6 py-2.5 text-sm font-bold text-white
                   transition-all duration-normal hover:scale-105 hover:shadow-[0_0_20px_rgba(37,211,102,0.4)] active:scale-95"
      >
        <WhatsAppIcon /> WhatsApp
      </a>
      <a
        href={VIBER_HREF}
        className="inline-flex items-center gap-2 rounded-pill bg-viber px-6 py-2.5 text-sm font-bold text-white
                   transition-all duration-normal hover:scale-105 hover:shadow-[0_0_20px_rgba(115,96,242,0.4)] active:scale-95"
      >
        <ViberIcon /> Viber
      </a>
    </div>
  )
}
